import { useState } from 'react';
import { Alert, Button, Group, Loader, Modal, NumberInput, Select, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../api/client.ts';
import { useApiQuery } from '../lib/hooks.ts';
import { fmtQty, fromInput, qtyInputProps } from '../lib/format.ts';
import { notifyError, notifySuccess } from '../lib/notify.ts';
import { BufferStoragePath, type BufferStorageCell } from './BufferStoragePath.tsx';

export function BufferCountModal({productId,warehouseId,onClose}:{productId:number;warehouseId:number;onClose:()=>void}) {
  const qc=useQueryClient();
  const query=useApiQuery<{name:string;physical:number;reserved:number;blocked:number;configured_buffer:number;version:string;
    cells:{address:string;quantity:number}[];storage_address?:string|null;storage_cells?:BufferStorageCell[]}>(['bufferCount',productId,warehouseId],`/stocks/${productId}/buffer-count${qs({warehouse_id:warehouseId})}`);
  const [actual,setActual]=useState<string|number>('');
  const [cell,setCell]=useState<string|null>(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState<string|null>(null);
  // Freeze payload on an ambiguous network failure; retry uses the same key and values.
  const [pending,setPending]=useState<any>(null);
  const d=query.data,grams=actual===''?null:fromInput(actual,'г');
  const delta=d&&grams!=null?grams-d.physical:null;
  const selected=cell ?? (d?.cells.length===1?d.cells[0].address:null);
  const save=async()=>{
    if(!d||grams==null)return;
    const body=pending??{warehouse_id:warehouseId,actual:grams,cell_address:selected??'NOCELL',version:d.version,request_id:crypto.randomUUID()};
    setPending(body);setBusy(true);setError(null);
    try {
      await api(`/stocks/${productId}/buffer-count`,{method:'POST',body});
      for(const key of ['buffer-alerts','stocks','stockDocs','stockDoc','stockRelatedKits','product']) void qc.invalidateQueries({queryKey:[key]});
      notifySuccess('Остаток сверен. Буфер снят до следующей проведённой приёмки.');onClose();
    } catch(e:any) {
      setError(e.message);notifyError('Сверка не сохранена',e.message);
      if(e.status && e.status<500){setPending(null);void query.refetch();}
    } finally {setBusy(false);}
  };
  return <Modal opened onClose={busy?()=>{}:onClose} title="Сверка фактического веса" closeOnClickOutside={!busy}>
    <Stack>
      {query.isLoading&&<Loader size="sm"/>}
      {query.isError&&<Alert color="red">Не удалось загрузить остаток.<Button onClick={()=>void query.refetch()}>Обновить</Button></Alert>}
      {d&&<>
        <Text fw={600}>{d.name}</Text>
        <BufferStoragePath assigned={d.storage_address} cells={d.storage_cells} unit="г" />
        <Text>По учёту: <b>{fmtQty(d.physical,'г')}</b>. В резерве: {fmtQty(d.reserved,'г')}. Заблокировано: {fmtQty(d.blocked,'г')}.</Text>
        <Alert color="blue">Взвесьте весь физический остаток товара на этом складе, включая резерв. Разница будет сразу проведена документом. Буфер {fmtQty(d.configured_buffer,'г')} включится при следующей проведённой приёмке этого товара.</Alert>
        <NumberInput label="Фактический остаток, кг" {...qtyInputProps('г')} min={0} value={actual} onChange={setActual} disabled={busy||!!pending} />
        {delta!=null&&<Text fw={600}>{delta===0?'Разницы нет — только снять буфер':`${delta<0?'Списание':'Оприходование'}: ${fmtQty(Math.abs(delta),'г')}`}</Text>}
        {delta!==null&&delta!==0&&<Select label="Ячейка для корректировки разницы" placeholder="Выберите ячейку" value={selected} onChange={setCell}
          disabled={busy||!!pending} data={d.cells.map(c=>({value:c.address,label:`${c.address} · ${fmtQty(c.quantity,'г')}`}))}/>}
        {delta!=null&&delta<0&&<Text size="xs" c="dimmed">Списание начнётся с выбранной ячейки. Если веса в ней не хватит, остаток разницы распределится по другим активным ячейкам хранения. Разбивка сохранится в документе.</Text>}
        {error&&<Alert color="red">{error}</Alert>}
        <Group justify="flex-end"><Button variant="default" disabled={busy} onClick={onClose}>Закрыть</Button>
          <Button loading={busy} disabled={grams==null||(!pending&&delta!==0&&!selected)} onClick={()=>void save()}>{pending?'Повторить сохранение':'Сохранить факт и снять буфер'}</Button></Group>
      </>}
    </Stack>
  </Modal>;
}
